import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';

// Register language
SyntaxHighlighter.registerLanguage('json', json);

const TestPage = () => {
  const { datasetId } = useParams();
  const { getAuthHeader } = useContext(AuthContext);
  
  const [dataset, setDataset] = useState(null);
  const [inputData, setInputData] = useState('');
  const [outputData, setOutputData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [datasetLoading, setDatasetLoading] = useState(true);
  const [datasetError, setDatasetError] = useState(null);
  const [testDestinations, setTestDestinations] = useState(false);
  const [testTransformer, setTestTransformer] = useState(false);
  const [destinationResults, setDestinationResults] = useState(null);
  const [transformerResults, setTransformerResults] = useState(null);

  // Load dataset info
  useEffect(() => {
    const fetchDataset = async () => {
      try {
        const config = {
          headers: getAuthHeader(),
        };

        // Using datasetId as the path parameter to match :id in the backend
        const { data } = await axios.get(`/api/datasets/${datasetId}`, config);
        setDataset(data);
        
        // Generate sample input based on schema
        if (data.schema) {
          const sampleData = {};
          Object.entries(data.schema).forEach(([field, type]) => {
            switch (type.toLowerCase()) {
              case 'string':
                sampleData[field] = 'Sample text';
                break;
              case 'number':
                sampleData[field] = 42;
                break;
              case 'boolean':
                sampleData[field] = true;
                break;
              case 'object':
                sampleData[field] = { sample: 'object' };
                break;
              case 'array':
                sampleData[field] = ['sample', 'array'];
                break;
              default:
                sampleData[field] = null;
            }
          });
          
          setInputData(JSON.stringify(sampleData, null, 2));
        } else {
          // If there's no schema, provide a simple example
          setInputData(JSON.stringify({ "email": "user@example.com", "name": "John Doe" }, null, 2));
        }
        
        setDatasetError(null);
      } catch (error) {
        console.error('Error fetching dataset:', error);
        setDatasetError(
          error.response?.data?.message || 'Failed to load dataset. Please try again.'
        );
      } finally {
        setDatasetLoading(false);
      }
    };

    fetchDataset();
  }, [datasetId, getAuthHeader]);

  const handleInputChange = (e) => {
    setInputData(e.target.value);
  };

  const handleTestDestinationsChange = (e) => {
    setTestDestinations(e.target.checked);
  };

  const handleTestTransformerChange = (e) => {
    setTestTransformer(e.target.checked);
  };

  const handleTest = async () => {
    try {
      setLoading(true);
      setError(null);
      setOutputData(null);
      setDestinationResults(null);
      setTransformerResults(null);
      
      // Parse input data
      let parsedData;
      try {
        parsedData = JSON.parse(inputData);
      } catch (e) {
        setError('Invalid JSON input. Please check your syntax.');
        setLoading(false);
        return;
      }

      const config = {
        headers: getAuthHeader(),
      };

      // Using datasetId parameter to match :id in the backend for the test endpoint
      const { data } = await axios.post(
        `/api/datasets/${datasetId}/test`,
        { 
          data: parsedData,
          testDestinations: testDestinations,
          testTransformer: testTransformer
        },
        config
      );

      setOutputData(data.enrichedData);
      
      // If destination results are available, set them
      if (data.destinationResults) {
        setDestinationResults(data.destinationResults);
      }

      // If transformer results are available, set them
      if (data.transformerResults) {
        setTransformerResults(data.transformerResults);
      }
    } catch (error) {
      console.error('Error testing dataset:', error);
      setError(
        error.response?.data?.message || 'Error processing data. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (datasetLoading) {
    return (
      <div className="flex justify-center my-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (datasetError) {
    return (
      <div className="container mx-auto max-w-4xl">
        <div className="bg-red-50 p-4 rounded-md my-6">
          <p className="text-red-600">{datasetError}</p>
        </div>
        <Link to="/" className="btn btn-primary">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-6xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Test Enrichment</h1>
        <Link
          to={`/datasets/${datasetId}`}
          className="btn btn-secondary"
        >
          Back to Dataset
        </Link>
      </div>

      {dataset && (
        <div className="bg-white shadow-md rounded-lg p-4 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-2">{dataset.name}</h2>
          <div className="mb-3">
            <div className="flex flex-wrap gap-2 mb-2">
              <span className="text-sm font-medium text-gray-700">Rules:</span>
              {dataset.rules.map((rule) => (
                <span
                  key={rule._id}
                  className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded"
                >
                  {rule.fieldName} ({rule.type})
                </span>
              ))}
              {dataset.rules.length === 0 && (
                <span className="text-sm text-gray-500">No enrichment rules defined</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium text-gray-700">Destinations:</span>
              {dataset.destinations.map((destination) => (
                <span
                  key={destination._id}
                  className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded"
                >
                  {destination.type} {destination.active ? '(active)' : '(inactive)'}
                </span>
              ))}
              {dataset.destinations.length === 0 && (
                <span className="text-sm text-gray-500">No destinations defined</span>
              )}
            </div>
          </div>

          {dataset.destinations.length > 0 && (
            <div className="flex items-center mt-3">
              <input
                id="test-destinations"
                type="checkbox"
                checked={testDestinations}
                onChange={handleTestDestinationsChange}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="test-destinations" className="ml-2 text-sm font-medium text-gray-700">
                Also test destinations
              </label>
            </div>
          )}

          {dataset.transformer && dataset.transformer !== 'none' && (
            <div className="flex items-center mt-3">
              <input
                id="test-transformer"
                type="checkbox"
                checked={testTransformer}
                onChange={handleTestTransformerChange}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="test-transformer" className="ml-2 text-sm font-medium text-gray-700">
                Test transformer ({dataset.transformer === 'tallyDataTransformer' ? 'Tally data transformer' : dataset.transformer})
              </label>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 p-4 rounded-md mb-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Input Data</h2>
            <button
              onClick={handleTest}
              disabled={loading || dataset?.rules.length === 0}
              className="btn btn-primary"
            >
              {loading ? 'Processing...' : testDestinations ? 'Test Enrichment & Destinations' : 'Test Enrichment'}
            </button>
          </div>

          <div className="border rounded-md">
            <textarea
              value={inputData}
              onChange={handleInputChange}
              className="w-full h-96 font-mono text-sm p-4 focus:outline-none focus:ring-0 border-0"
              placeholder="Paste your JSON data here..."
            />
          </div>

          <div className="mt-2 text-xs text-gray-500">
            <p>Enter JSON data that matches your dataset's schema.</p>
          </div>
        </div>

        <div>
          <div className="card mb-6">
            <h2 className="text-lg font-medium mb-4">Enriched Output</h2>

            {loading ? (
              <div className="flex items-center justify-center h-96 border rounded-md bg-gray-50">
                <div className="animate-pulse text-gray-400">Processing data...</div>
              </div>
            ) : outputData ? (
              <div className="border rounded-md overflow-auto h-96">
                <SyntaxHighlighter
                  language="json"
                  style={atomOneDark}
                  customStyle={{ margin: 0, height: '100%' }}
                >
                  {JSON.stringify(outputData, null, 2)}
                </SyntaxHighlighter>
              </div>
            ) : (
              <div className="flex items-center justify-center h-96 border rounded-md bg-gray-50">
                <div className="text-gray-400">
                  {dataset?.rules.length === 0
                    ? 'Add rules to your dataset before testing'
                    : 'Click "Test Enrichment" to see results'}
                </div>
              </div>
            )}
          </div>

          {testDestinations && (
            <div className="card mb-6">
              <h2 className="text-lg font-medium mb-4">Destination Results</h2>
              
              {loading ? (
                <div className="flex items-center justify-center h-48 border rounded-md bg-gray-50">
                  <div className="animate-pulse text-gray-400">Processing destinations...</div>
                </div>
              ) : destinationResults ? (
                <div className="border rounded-md overflow-auto max-h-96">
                  <SyntaxHighlighter
                    language="json"
                    style={atomOneDark}
                    customStyle={{ margin: 0 }}
                  >
                    {JSON.stringify(destinationResults, null, 2)}
                  </SyntaxHighlighter>
                </div>
              ) : (
                <div className="flex items-center justify-center h-48 border rounded-md bg-gray-50">
                  <div className="text-gray-400">
                    {dataset?.destinations.length === 0
                      ? 'Add destinations to your dataset before testing'
                      : 'Destination results will appear here'}
                  </div>
                </div>
              )}
            </div>
          )}

          {testTransformer && (
            <div className="card">
              <h2 className="text-lg font-medium mb-4">Transformer Results</h2>
              
              {loading ? (
                <div className="flex items-center justify-center h-48 border rounded-md bg-gray-50">
                  <div className="animate-pulse text-gray-400">Processing transformer...</div>
                </div>
              ) : transformerResults ? (
                <div className="border rounded-md overflow-auto max-h-96">
                  <SyntaxHighlighter
                    language="json"
                    style={atomOneDark}
                    customStyle={{ margin: 0 }}
                  >
                    {JSON.stringify(transformerResults, null, 2)}
                  </SyntaxHighlighter>
                </div>
              ) : (
                <div className="flex items-center justify-center h-48 border rounded-md bg-gray-50">
                  <div className="text-gray-400">
                    {!dataset?.transformer || dataset?.transformer === 'none'
                      ? 'Add a transformer to your dataset before testing'
                      : 'Transformer results will appear here'}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestPage;